// Minimal typed surface of Google Identity Services' OAuth2 code-client API
// (https://accounts.google.com/gsi/client), loaded as a script tag rather
// than an npm package — GIS has no first-party or DefinitelyTyped types.
// Only the code-client flow is typed here since that's the only part used:
// it's what yields an authorization code exchangeable server-side for both
// identity and a Drive-scoped refresh token in one consent (see
// openspec/changes/add-google-sso-drive-upload/design.md).

interface CodeClientConfig {
  client_id: string;
  scope: string;
  ux_mode: 'popup';
  callback: (response: CodeResponse) => void;
  error_callback?: (error: { type: string }) => void;
}

interface CodeResponse {
  code: string;
  scope: string;
  error?: string;
}

interface CodeClient {
  requestCode: () => void;
}

interface TokenClientConfig {
  client_id: string;
  scope: string;
  callback: (response: TokenResponse) => void;
  error_callback?: (error: { type: string }) => void;
}

interface TokenResponse {
  access_token: string;
  error?: string;
}

interface TokenClient {
  requestAccessToken: () => void;
}

interface GoogleAccountsOAuth2 {
  initCodeClient: (config: CodeClientConfig) => CodeClient;
  initTokenClient: (config: TokenClientConfig) => TokenClient;
}

// The Google Picker API (see googlePicker.ts) attaches its classes to this
// same `window.google` global under a `picker` key, hence the shared
// `GoogleGlobal` shape declared once here rather than two independent
// `declare global` blocks racing to describe the same property.
export interface GoogleGlobal {
  accounts: {
    oauth2: GoogleAccountsOAuth2;
  };
  picker?: unknown;
}

declare global {
  interface Window {
    google?: GoogleGlobal;
  }
}

const GIS_SCRIPT_SRC = 'https://accounts.google.com/gsi/client';
const DRIVE_AND_IDENTITY_SCOPES = [
  'openid',
  'https://www.googleapis.com/auth/userinfo.email',
  'https://www.googleapis.com/auth/userinfo.profile',
  'https://www.googleapis.com/auth/drive.file',
].join(' ');

let scriptLoadPromise: Promise<void> | null = null;

function loadGisScript(): Promise<void> {
  if (window.google?.accounts?.oauth2) return Promise.resolve();
  if (scriptLoadPromise) return scriptLoadPromise;

  scriptLoadPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = GIS_SCRIPT_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Google Identity Services script'));
    document.head.appendChild(script);
  });

  return scriptLoadPromise;
}

// requestGoogleAuthCode triggers the Google OAuth2 code-client popup flow
// (identity + drive.file scope, combined into one consent) and resolves with
// the authorization code the backend exchanges for tokens.
export async function requestGoogleAuthCode(clientId: string): Promise<string> {
  await loadGisScript();

  if (!window.google?.accounts?.oauth2) {
    throw new Error('Google Identity Services failed to load');
  }

  return new Promise<string>((resolve, reject) => {
    const client = window.google!.accounts.oauth2.initCodeClient({
      client_id: clientId,
      scope: DRIVE_AND_IDENTITY_SCOPES,
      ux_mode: 'popup',
      callback: (response) => {
        if (response.error || !response.code) {
          reject(new Error(response.error || 'Google sign-in was cancelled'));
          return;
        }
        resolve(response.code);
      },
      error_callback: (error) => {
        reject(new Error(`Google sign-in failed: ${error.type}`));
      },
    });

    client.requestCode();
  });
}

// requestDrivePickerAccessToken gets a short-lived, browser-only access token
// (the implicit token flow, not the code flow above) scoped just for the
// Google Picker to list/search the signed-in user's Drive. This token is
// used only client-side to render the Picker UI — it is never sent to the
// backend; the backend downloads file content later using its own
// separately-stored refresh token for this user (see design.md decision 1).
export async function requestDrivePickerAccessToken(clientId: string): Promise<string> {
  await loadGisScript();

  if (!window.google?.accounts?.oauth2) {
    throw new Error('Google Identity Services failed to load');
  }

  return new Promise<string>((resolve, reject) => {
    const client = window.google!.accounts.oauth2.initTokenClient({
      client_id: clientId,
      scope: 'https://www.googleapis.com/auth/drive.file',
      callback: (response) => {
        if (response.error || !response.access_token) {
          reject(new Error(response.error || 'Google Drive access was not granted'));
          return;
        }
        resolve(response.access_token);
      },
      error_callback: (error) => {
        reject(new Error(`Google Drive access failed: ${error.type}`));
      },
    });

    client.requestAccessToken();
  });
}
